import { setApiToken } from "@/services/api"
import { axiosClient, CLIENT_API } from "@/services/axios-client"
import { getJwtToken } from "@/services/getJWT"
import { useNotificationStore } from "@/states/zustand/notification"
import { useUserStore } from "@/states/zustand/user"
import { useMutation } from "@tanstack/react-query"
import { useNavigate } from "react-router"

export const useLogin = () => {

    const nav = useNavigate();

    const { mutate, isPending } = useMutation({
        mutationKey: ["login"],
        mutationFn: (data: LoginRequest) => CLIENT_API.login(data),
        onSuccess: (data) => {
            getJwtToken().setJwtToken(data);
            useUserStore.getState().setJwt(data);
            setApiToken({
                axiosInstance: axiosClient,
                token: data.token,
            })
            nav("/", { replace: true });
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                message: error,
                type: "error",
            })
        }
    });

    return {
        mutate,
        isLoading: isPending,
    }
}

export const useSendNotification = () => {
    const { mutate, isPending } = useMutation({
        mutationKey: ["sendNotification"],
        mutationFn: (data: SendNotificationRequest) => CLIENT_API.sendNotification(data),
        onSuccess: (data) => {
            useNotificationStore.getState().setNotification({
                type: "success",
                message: data?.message ?? "Notification sent successfully!"
            });
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "Failed to send notification"
            });
        }
    });

    return {
        mutate,
        isLoading: isPending,
    }
}

export const useCreateAdminAccount = () => {
    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["createAdminAccount"],
        mutationFn: (data: CreateAdminAccountRequest) => CLIENT_API.createAdminAccount(data),
        onSuccess: (data) => {
            useNotificationStore.getState().setNotification({
                type: "success",
                message: data?.message ?? "Admin account created successfully!"
            });
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "Failed to create admin account"
            });
        }
    });

    return {
        mutate,
        isMutating: isPending,
        ...rest
    }
}

export const useCheckPhoneNumber = () => {
    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["checkPhoneNumber"],
        mutationFn: (data: { phone: string }) => CLIENT_API.checkPhoneNumber(data),
        onSuccess: (data) => {
            useNotificationStore.getState().setNotification({
                type: "success",
                message: `OTP code has been sent to ${data.phone}. Please check your phone.`
            });
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "Failed to check phone number"
            });
        }
    });

    return {
        mutate,
        isMutating: isPending,
        ...rest
    }
}

export const useVerifyPhoneNumber = () => {
    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["verifyPhoneNumber"],
        mutationFn: (data: VerifyPhoneNumberRequest) => CLIENT_API.verifyPhoneNumber(data),
        onSuccess: (data) => {
            setApiToken({
                axiosInstance: axiosClient,
                token: data.jwt,
            });
            useNotificationStore.getState().setNotification({
                type: "success",
                message: "OTP verified successfully! Please create a new password."
            });
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "Failed to verify phone number"
            });
        }
    });

    return {
        mutate,
        isMutating: isPending,
        ...rest
    }
}

export const useCreateNewPassword = () => {

    const nav = useNavigate();

    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["createNewPassword"],
        mutationFn: (password: string) => CLIENT_API.createNewPassword(password),
        onSuccess: (data) => {
            useNotificationStore.getState().setNotification({
                type: "success",
                message: data?.message ?? "Password created successfully! Please login again."
            });
            nav("/login", { replace: true });
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "Failed to create new password"
            });
        }
    });

    return {
        mutate,
        isMutating: isPending,
        ...rest
    }
}